import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { ModifierDto } from './types';
import { useCreateModifier, useUpdateModifier } from './modifierGroupsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  groupId: string;
  modifier: ModifierDto | null;
}

interface FormValues {
  nameAr: string;
  nameEn: string;
  priceDelta: number;
  isActive: boolean;
  sortOrder: number;
}

export function ModifierFormModal({ opened, onClose, groupId, modifier }: Props) {
  const { t } = useTranslation();
  const create = useCreateModifier();
  const update = useUpdateModifier();
  const submitting = create.isPending || update.isPending;
  const editing = modifier !== null;

  const form = useForm<FormValues>({
    initialValues: { nameAr: '', nameEn: '', priceDelta: 0, isActive: true, sortOrder: 0 },
    validate: {
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      nameAr: modifier?.nameAr ?? '',
      nameEn: modifier?.nameEn ?? '',
      priceDelta: modifier?.priceDelta ?? 0,
      isActive: modifier?.isActive ?? true,
      sortOrder: modifier?.sortOrder ?? 0,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, modifier]);

  const submit = async (values: FormValues) => {
    const payload = {
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      priceDelta: values.priceDelta,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          groupId,
          modifierId: modifier.modifierId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync({ groupId, request: payload });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? t('modifiers.form.editTitle') : t('modifiers.form.createTitle')}
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('modifiers.form.nameArLabel')}
            placeholder={t('modifiers.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('modifiers.form.nameEnLabel')}
            placeholder={t('modifiers.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <NumberInput
            label={t('modifiers.form.priceDeltaLabel')}
            description={t('modifiers.form.priceDeltaHelp')}
            step={0.5}
            decimalScale={2}
            {...form.getInputProps('priceDelta')}
          />
          <NumberInput
            label={t('modifiers.form.sortOrderLabel')}
            description={t('modifiers.form.sortOrderHelp')}
            min={0}
            step={1}
            {...form.getInputProps('sortOrder')}
          />
          {editing && (
            <Switch
              label={t('modifiers.form.activeLabel')}
              description={t('modifiers.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
